import { KeyValuePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Input,
  LOCALE_ID,
  signal,
  untracked,
  ViewEncapsulation
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import { NumberPipe } from '@core/common/pipes';
import { ConvertDirective } from '@core/converter';
import { isObject, uniqId } from '@core/helpers';
import { WebsocketClient } from '@core/websocket';
import { environment } from '@env';

import { AuthState } from '../../../modules/users';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';

@Component({
  selector: 'shell-footer',
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': `'footer '+ (gameModule ? 'footer_'+ gameModule : '')`,
    '[class.footer_stretch]': 'isStretch'
  },
  imports: [
    LanguageSelectorComponent,
    RouterLink,
    KeyValuePipe,
    NumberPipe
  ]
})
export class FooterComponent {
  public readonly authState = inject(AuthState);
  public readonly websocketClient = inject(WebsocketClient);
  public readonly localeId = inject(LOCALE_ID);
  public readonly isRuLanguage = 'ru' === inject(LOCALE_ID);
  public readonly is6csfail =
    typeof window !== 'undefined' &&
    (window.location.href.includes('6cs.fail') || window.location.href.includes('7cs.fail'));

  public readonly currentYear = new Date().getFullYear();

  @Input()
  public isStretch!: boolean;

  @Input()
  public gameModule!: string | null;

  public readonly online = signal<number | undefined>(undefined);

  private readonly websocketConnectedEffect = effect(() => {
    const isConnected = this.websocketClient.isConnected();

    if (isConnected) {
      untracked(() => this.fetchOnline());
    }
  });

  public readonly socialLinks = this.getSocialLinks();
  public readonly socialIcons: Record<string, string> = {
    vk: 'icon_vk',
    facebook: 'icon_facebook',
    twitter: 'icon_Twitter',
    telegram: 'icon_tg',
    discord: 'icon_discord',
    youtube: 'icon_ytb',
    instagram: 'icon_instagram'
  };

  constructor() {
    this.listenWebsocketOnline();
  }

  private getSocialLinks(): Record<string, string> {
    if ('ru' === this.localeId || 'uk' === this.localeId) {
      if ('uk' === this.localeId) {
        // Создаем новый объект без свойства vk, используя деструктуризацию
        const { vk, ...socialsCopy } = environment.socials['cis'];
        return socialsCopy;
      }

      return environment.socials['cis'];
    } else if ('pl' === this.localeId) {
      return environment.socials['pl'];
    }

    return environment.socials['eu'];
  }

  private fetchOnline(): void {
    const message = {
      id: uniqId(),
      event: 'online',
      data: null
    };

    this.websocketClient
      .send<null, number>(message, true)
      .pipe(map((response) => response!.data))
      .subscribe((online) => this.online.set(online));
  }

  private listenWebsocketOnline(): void {
    this.websocketClient.stream$
      .pipe(filter((message): message is number => isObject(message) && message['type'] === 'online'))
      .subscribe((online) => this.online.set(online));
  }
}
